<template>
  <div>
    <ts-card>
      <div class="form-header ts-bg-gray-100 ts-border-b ts-border-gray-400 ts-p-4 ts-relative">
        <h4 class="ts-text-sm">{{ $t('basicSalaryFormula.pageTitle') }}</h4>
        <ts-modal-close-button />
      </div>
    </ts-card>
    <ts-card-wrapper>
      <form class="horizontal">
        <div class="note note-primary">
          <div class="note-content">
            <h4>
              <b>{{ $t('basicSalaryFormula.parameters') }} <i class="fas fa-spinner spin" v-if="loadParameter"></i> </b>
              <i
                class="fas fa-question-circle ml-2"
                v-tooltip="'Use these parameters to create formula.'"
                v-if="!loadParameter"
              ></i>
            </h4>
            <div class="row mt-2">
              <div class="col-md-3 col-sm-4" v-for="(param, index) in parameters" :key="index">
                <p
                  class="parameter"
                  v-clipboard:copy="param.param_name"
                  v-clipboard:success="onCopy"
                  v-clipboard:error="onError"
                  v-tooltip="copied"
                >
                  {{ param.param_name }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="row form-group m-b-15">
          <label class="col-md-3 col-form-label required">{{ $t('basicSalaryFormula.salaryPerDay') }}</label>
          <div class="col-md-9">
            <input
              v-model="model.salary_per_day"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.has('salary_per_day') }"
            />
            <i class="ts-gray-400"><small>( e.g {base_salary} * {total_days} )</small></i>
            <div class="invalid-feedback" v-if="errors.has('salary_per_day')">
              {{ errors.first('salary_per_day') }}
            </div>
          </div>
        </div>
        <div class="row form-group m-b-15">
          <label class="col-md-3 col-form-label required">{{ $t('basicSalaryFormula.salaryPerHours') }}</label>
          <div class="col-md-9">
            <input
              v-model="model.salary_per_hour"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.has('salary_per_hour') }"
            />
            <i class="ts-gray-400"><small>( e.g {base_salary} * {total_hours} )</small></i>
            <div class="invalid-feedback" v-if="errors.has('salary_per_hour')">
              {{ errors.first('salary_per_hour') }}
            </div>
          </div>
        </div>
        <div class="row form-group m-b-15">
          <label class="col-md-3 col-form-label">{{ $t('basicSalaryFormula.endOfMonth') }}</label>
          <div class="col-md-9">
            <ts-checkbox v-model="model.is_end_of_month"></ts-checkbox>
          </div>
        </div>
        <div class="row form-group m-b-15">
          <label class="col-md-3 col-form-label" :class="{ required: !model.is_end_of_month }">{{
            $t('basicSalaryFormula.totalDay')
          }}</label>
          <div class="col-md-9">
            <input
              v-model.number="model.total_days"
              type="number"
              class="form-control"
              :class="{ 'is-invalid': errors.has('total_days') }"
              :disabled="model.is_end_of_month"
            />
            <div class="invalid-feedback" v-if="errors.has('total_days')">
              {{ errors.first('total_days') }}
            </div>
          </div>
        </div>
        <div class="row form-group m-b-15">
          <label class="col-md-3 col-form-label" :class="{ required: !model.is_end_of_month }">{{
            $t('basicSalaryFormula.totalHours')
          }}</label>
          <div class="col-md-9">
            <input
              v-model.number="model.total_hours"
              type="number"
              class="form-control"
              :class="{ 'is-invalid': errors.has('total_hours') }"
              :disabled="model.is_end_of_month"
            />
            <div class="invalid-feedback" v-if="errors.has('total_hours')">
              {{ errors.first('total_hours') }}
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12 text-right">
            <ts-button @click.prevent="$emit('close')">
              {{ $t('button.cancel') }}
            </ts-button>
            <ts-button @click.prevent="update" class="btn-primary ml-2" :waiting="saving">
              {{ $t('button.update') }}
            </ts-button>
          </div>
        </div>
      </form>
    </ts-card-wrapper>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { Errors } from 'form-backend-validation'
import formVue from '../job-title/form.vue'

export default {
  props: ['editData'],
  name: 'basic-salary-formula-form',
  data() {
    return {
      model: {
        shift_id: '',
        salary_per_day: '',
        salary_per_hour: '',
        is_end_of_month: false,
        total_days: '',
        total_hours: ''
      },
      errors: new Errors(),
      loadParameter: false,
      saving: false,
      copied: this.$t('basicSalaryFormula.copy_parameters')
    }
  },
  created() {
    this.fetchParam(), this.setData()
  },
  computed: {
    ...mapState('hr/basicSalaryFormula', ['parameters'])
  },
  methods: {
    fetchParam() {
      this.loadParameter = true
      this.$store
        .dispatch('hr/basicSalaryFormula/getParameter')
        .then(response => {
          this.loadParameter = false
        })
        .catch(error => {
          this.loadParameter = false
          this.$toasted.error(error.message)
        })
    },
    update() {
      this.errors = new Errors()
      this.saving = true
      this.$store
        .dispatch('hr/basicSalaryFormula/update', {
          id: this.editData.shift_id,
          data: this.model
        })
        .then(response => {
          this.saving = false
          this.$toasted.success(response.message)
          this.$emit('close')
          this.fetchData()
        })
        .catch(error => {
          this.saving = false
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
    },
    setData() {
      if (!_.isEmpty(this.editData)) {
        this.model = {
          shift_id: this.editData.shift_id,
          salary_per_day: this.editData.salary_per_day,
          salary_per_hour: this.editData.salary_per_hour,
          is_end_of_month: this.editData.is_end_of_month ? true : false,
          total_days: this.editData.total_days,
          total_hours: this.editData.total_hours
        }
      }
    },
    fetchData() {
      this.$store.dispatch('hr/basicSalaryFormula/fetch').catch(error => {
        this.$toasted.error(error.message)
      })
    },
    onCopy: function(e) {
      this.copied = this.$t('basicSalaryFormula.copied')
      setTimeout(() => {
        this.copied = this.$t('basicSalaryFormula.copy_parameters')
      }, 1000)
    },
    onError: function(e) {
      this.copied = this.$t('basicSalaryFormula.failed_copied')
    }
  },
  watch: {
    'model.is_end_of_month': function(value) {
      if (value) {
        ;(this.model.total_days = ''), (this.model.total_hours = '')
      }
    }
  }
}
</script>

<style lang="css" scoped>
.parameter {
	cursor: pointer;
}
</style>
